import { AuthError } from "@shared/feShared/auth/tokenExchange";
import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { acceptTermsAndConditionsMutation } from "@/auth/api/mutations";
import { currentUserAuthQuery, termsAndConditionsQuery } from "@/auth/api/queries";
import { authClient, getBootstrapPromise } from "@/auth/authClientInstance";
import { type FormValues } from "@/auth/schema";
import { activeBusinessUnitIdVar, businessUnitsQuery } from "@/hooks/use-active-business-unit";
import { apolloClient } from "@/lib/apollo-client";
import { clearAuthLocalStorage } from "@/lib/storage";
import { getErrorMessage } from "@/lib/utils/errors";

const ACTIVE_BUSINESS_UNIT_KEY = "businessUnitId";

export type Terms = { id: number; title: string; description: string };

export type LoginResult =
  | { success: true; requiresTermsAccept: false }
  | { success: true; requiresTermsAccept: true; terms: Terms; businessUnitId: number }
  | { success: false; error: string };

export type AcceptTermsResult = { success: true } | { success: false; error: string };

export type AuthState = {
  login: (credentials: FormValues) => Promise<LoginResult>;
  acceptTermsAndConditions: (termsId: number, businessUnitId: number) => Promise<AcceptTermsResult>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
};

const AuthContext = createContext<AuthState | undefined>(undefined);

const computeIsAuthenticated = (): boolean => {
  // Normal case: a real access token is in memory (legacy hydrated from
  // localStorage, or Cognito set by signIn / refresh).
  const token = authClient.getAccessToken();
  if (token && token.length > 0) {
    return true;
  }
  // No token in memory. The only legitimate reason for this is a Cognito
  // page-refresh: the access token isn't persisted, and the bootstrap
  // refresh (see `authClientInstance.ts`) is still in flight. Trust the
  // refreshable session type only while that promise is pending — once
  // it resolves, authClient will either hold a token (success) or
  // `onSessionExpired` will have cleared the session type (failure).
  return authClient.getSessionType() === "refreshable" && getBootstrapPromise() !== null;
};

const setActiveBusinessUnitFromLogin = async (): Promise<number | null> => {
  const buData = await apolloClient.query({
    query: businessUnitsQuery,
    fetchPolicy: "network-only",
  });
  const firstBu = buData.data.businessUnits?.[0];
  if (!firstBu) {
    return null;
  }
  localStorage.setItem(ACTIVE_BUSINESS_UNIT_KEY, firstBu.id);
  activeBusinessUnitIdVar(firstBu.id);
  return Number(firstBu.id);
};

const loadTerms = async (): Promise<Terms | null> => {
  const result = await apolloClient.query({
    query: termsAndConditionsQuery,
    variables: { tacType: "BASIC" },
    fetchPolicy: "network-only",
  });
  const terms = result.data.termsAndConditions;
  if (!terms) return null;
  return {
    id: Number(terms.id),
    title: terms.title ?? "",
    description: terms.description ?? "",
  };
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(computeIsAuthenticated);

  // After the bootstrap refresh settles, re-derive isAuthenticated so route
  // guards see the resolved state. Without this, a Cognito user reloading
  // the page would render `isAuthenticated = true` (refreshable session
  // type, bootstrap pending) → after bootstrap fails the access token is
  // still empty and the session type has been cleared by `onSessionExpired`,
  // but React state wouldn't reflect that until the next event.
  useEffect(() => {
    const pending = getBootstrapPromise();
    if (!pending) return;
    void pending.finally(() => {
      setIsAuthenticated(computeIsAuthenticated());
    });
  }, []);

  const login = useCallback(async (credentials: FormValues): Promise<LoginResult> => {
    try {
      await authClient.signIn(credentials.email, credentials.password);
    } catch (error) {
      if (error instanceof AuthError) {
        return { success: false, error: getErrorMessage(error) };
      }
      return { success: false, error: getErrorMessage(error) };
    }

    try {
      const userResult = await apolloClient.query({
        query: currentUserAuthQuery,
        fetchPolicy: "network-only",
      });

      const user = userResult.data.currentUser;
      if (!user) {
        return { success: false, error: "Failed to load user." };
      }

      const businessUnitId = await setActiveBusinessUnitFromLogin();

      if (user.isTermsAccepted) {
        setIsAuthenticated(true);
        return { success: true, requiresTermsAccept: false };
      }

      if (businessUnitId == null) {
        return { success: false, error: "No active business unit." };
      }

      const terms = await loadTerms();
      if (!terms) {
        return { success: false, error: "Unable to load terms and conditions." };
      }

      return { success: true, requiresTermsAccept: true, terms, businessUnitId };
    } catch (error) {
      return { success: false, error: getErrorMessage(error) };
    }
  }, []);

  const acceptTermsAndConditions = useCallback(
    async (termsId: number, businessUnitId: number): Promise<AcceptTermsResult> => {
      try {
        const result = await apolloClient.mutate({
          mutation: acceptTermsAndConditionsMutation,
          variables: { input: { id: termsId, businessUnitId } },
        });
        if (!result.data?.termsAndConditionsAccept?.success) {
          return { success: false, error: "Could not accept terms and conditions." };
        }
        setIsAuthenticated(true);
        return { success: true };
      } catch (error) {
        return { success: false, error: getErrorMessage(error) };
      }
    },
    [],
  );

  const logout = useCallback(async () => {
    try {
      await authClient.logout();
    } catch (error) {
      console.log("error calling logout endpoint:", error);
    }
    clearAuthLocalStorage();
    localStorage.clear();
    await apolloClient.clearStore();
    setIsAuthenticated(false);
  }, []);

  const value: AuthState = { login, acceptTermsAndConditions, logout, isAuthenticated };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
